import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usePageContext } from '../context/PageContext';
import { supabase, FUNCTIONS_URL } from '../lib/supabase';
import { X, Check, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, FileText, CheckSquare, Square, Search, ExternalLink, Shield, AlertTriangle, CheckCircle, XCircle, Loader2, EyeOff, Eye, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { defaultPolicy } from '../lib/issuePolicy';
import {
  ISSUE_SELECT_COLUMNS, LATENESS_CATEGORY, countableIssues,
  suppressedIssues as suppressedIssuesIn,
  OPEN_STATUSES, UNDER_REVIEW_STATUSES, CLOSED_STATUSES,
} from '../lib/issueCounting';
import { normalizeOutcomeOptions } from '../lib/issueOutcomes';
import { loadTutorReviewStates } from '../lib/tutorReview';

export const CATEGORIES = [
  { key: 'inappropriate_discussion', label: 'Inappropriate Discussion' },
  { key: 'poaching_attempt', label: 'Poaching Risk' },
  { key: 'disparagement_of_organization', label: 'Disparagement' },
  { key: 'factual_teaching_error', label: 'Teaching Error' },
];

const STATUSES = [
  { key: 'open', label: 'Open' },
  { key: 'under_review', label: 'Under Review' },
  { key: 'closed', label: 'Closed' },
];

const PER_CATEGORY_PAGE = 20;
// PostgREST caps a response at 1000 rows, so every org-wide read is paged.
const ROW_PAGE_SIZE = 1000;
const ROW_PAGE_LIMIT = 50;

// Reads every page of a risks_v2 query. `build` gets a fresh query each time so
// the range can be applied; ordering is stable so pages can't overlap or skip.
const fetchAllPages = async (build) => {
  const rows = [];
  for (let p = 0; p < ROW_PAGE_LIMIT; p++) {
    const from = p * ROW_PAGE_SIZE;
    const { data, error } = await build().range(from, from + ROW_PAGE_SIZE - 1);
    if (error) { console.error('risks_v2 page failed:', error); break; }
    if (!data) break;
    rows.push(...data);
    if (data.length < ROW_PAGE_SIZE) break;
  }
  return rows;
};

// The three tabs are the status buckets of lib/issueCounting.js, and these
// predicates mirror the per-tab queries in loadIssues exactly — so a tab's
// count is always the length of the list it opens. A status outside all three
// lists (the legacy 'actioned' / 'non_issue' values) has no tab on this page
// and is left out rather than inflating a count whose list omits it.
const tabForStatus = (status) => {
  const s = (status || 'new').toLowerCase();
  if (OPEN_STATUSES.includes(s)) return 'open';
  if (UNDER_REVIEW_STATUSES.includes(s)) return 'under_review';
  if (CLOSED_STATUSES.includes(s)) return 'closed';
  return null;
};
const CARE_LABELS = ['No Answer', 'Not at all', 'Slightly', 'Somewhat', 'Quite a bit', 'A lot'];
const BULK_ACTIONS = [
  { value: '', label: 'Bulk action...' },
  { value: 'dismiss', label: 'Dismiss selected' },
  { value: 'under_review', label: 'Mark as under review' },
  { value: 'resolved', label: 'Mark as resolved' },
];

const Issues = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setSubtitle } = usePageContext();
  const isDark = theme === 'dark';

  // The status tab and open issue live in the URL (?tab=…&issue=…) so going
  // back from a lesson/transcript returns to the same issue, and links can be
  // opened in a new tab.
  const [statusTab, setStatusTab] = useState(() => {
    const tab = searchParams.get('tab');
    return ['open', 'under_review', 'closed'].includes(tab) ? tab : 'open';
  });
  const pendingIssueId = useRef(searchParams.get('issue'));
  const [allIssues, setAllIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedIssueIdx, setSelectedIssueIdx] = useState(0);
  const [page, setPage] = useState(0);
  const [statusCounts, setStatusCounts] = useState({ open: 0, under_review: 0, closed: 0 });
  const [careLevel, setCareLevel] = useState(0);
  const [proposedOutcome, setProposedOutcome] = useState('');
  const [saving, setSaving] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set(['inappropriate_discussion']));
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categoryPages, setCategoryPages] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [breaches, setBreaches] = useState([]);
  const [breachLoading, setBreachLoading] = useState(false);
  const [breachRunning, setBreachRunning] = useState(false);
  const [breachResult, setBreachResult] = useState(null);
  const [breachResultAt, setBreachResultAt] = useState(null);
  const [libraryReadyCount, setLibraryReadyCount] = useState(0);
  const [curriculumReadyCount, setCurriculumReadyCount] = useState(0);
  const [curriculumRunning, setCurriculumRunning] = useState(false);
  const [curriculumResult, setCurriculumResult] = useState(null);
  const [curriculumResultAt, setCurriculumResultAt] = useState(null);
  const [policy, setPolicy] = useState(defaultPolicy());
  const [suppressedCount, setSuppressedCount] = useState(0);
  const [showSuppressed, setShowSuppressed] = useState(false);
  const [suppressedIssues, setSuppressedIssues] = useState([]);
  const [outcomeOptions, setOutcomeOptions] = useState(normalizeOutcomeOptions());
  const [actionedByOpen, setActionedByOpen] = useState(false);
  const [actionedByNames, setActionedByNames] = useState([]);
  const [actionedByNew, setActionedByNew] = useState('');
  const [addingNewName, setAddingNewName] = useState(false);
  // { [tutor_name]: 'under_review' | 'open' | 'resolved' } — see lib/tutorReview.js
  const [tutorStates, setTutorStates] = useState({});

  const loadActionedByNames = async () => {
    if (!organizationId) return;
    const { data } = await supabase
      .from('risks_v2')
      .select('actioned_by')
      .eq('organization_id', organizationId)
      .not('actioned_by', 'is', null)
      .limit(500);
    const names = Array.from(new Set((data || []).map(r => (r.actioned_by || '').trim()).filter(Boolean))).sort();
    setActionedByNames(names);
  };

  const resolveAsActioned = async (name) => {
    if (!selectedIssue || !name?.trim()) return;
    try {
      const { error } = await supabase
        .from('risks_v2')
        .update({
          status: 'resolved',
          actioned_by: name.trim(),
          actioned_at: new Date().toISOString(),
          resolved_at: new Date().toISOString(),
        })
        .eq('id', selectedIssue.id);
      if (error) throw error;
      toast.success(`Actioned by ${name.trim()}`);
      setActionedByOpen(false);
      setActionedByNew('');
      setAddingNewName(false);
      loadIssues();
      loadAllStatusCounts();
    } catch (err) {
      console.error(err);
      const parts = [err?.code, err?.message, err?.details].filter(Boolean);
      toast.error(`Failed: ${parts.join(' — ') || 'unknown error'}`);
    }
  };

  useEffect(() => {
    if (!organizationId) return;
    loadLibraryReadyCount();
  }, [organizationId]);

  const loadLibraryReadyCount = async () => {
    try {
      const { count } = await supabase
        .from('policy_documents')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .eq('status', 'ready')
        .eq('source', 'library');
      setLibraryReadyCount(count || 0);
    } catch (err) {
      setLibraryReadyCount(0);
    }
    try {
      const { count } = await supabase
        .from('policy_documents')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .eq('status', 'ready')
        .eq('source', 'library')
        .eq('document_type', 'curriculum');
      setCurriculumReadyCount(count || 0);
    } catch (err) {
      setCurriculumReadyCount(0);
    }
  };

  useEffect(() => {
    if (!organizationId) return;
    loadAllStatusCounts();
  }, [organizationId]);

  // Tab counts are taken from the same rules as the lists: lib/issueCounting.js
  // decides what is countable (never tutor_lateness, never a guardrail-
  // suppressed row), and tabForStatus mirrors the per-tab queries below.
  const loadAllStatusCounts = async () => {
    try {
      const [{ data: orgRow }, rows] = await Promise.all([
        supabase.from('organizations').select('issue_filter_policy').eq('id', organizationId).maybeSingle(),
        fetchAllPages(() => supabase
          .from('risks_v2')
          .select(ISSUE_SELECT_COLUMNS)
          .eq('organization_id', organizationId)
          .neq('category', LATENESS_CATEGORY)
          .order('id')),
      ]);
      const pol = orgRow?.issue_filter_policy || defaultPolicy();
      const counts = { open: 0, under_review: 0, closed: 0 };
      countableIssues(rows, pol).forEach(r => {
        const tab = tabForStatus(r.status);
        if (tab) counts[tab] += 1;
      });
      setStatusCounts(counts);
    } catch (err) {
      console.error('Error loading status counts:', err);
    }
  };

  useEffect(() => {
    if (!organizationId) return;
    loadIssues();
  }, [organizationId, statusTab]);

  const loadIssues = async () => {
    setLoading(true);
    try {
      // Status buckets come from lib/issueCounting.js so the tab a row lands in
      // is decided in one place; tutor_lateness is dropped in SQL because
      // risks_v2.category is NOT NULL, which makes it exactly the helper's rule.
      const buildQuery = () => {
        let query = supabase
          .from('risks_v2')
          .select('*, sessions!inner(id, tutor_name, student_name, lesson_timestamp, session_id, duration_minutes)')
          .eq('organization_id', organizationId)
          .neq('category', LATENESS_CATEGORY)
          .order('created_at', { ascending: false })
          .order('id');

        if (statusTab === 'open') {
          // status is NOT NULL in schema, but tolerate a null like the helper does.
          query = query.or(['status.is.null', ...OPEN_STATUSES.map(s => `status.eq.${s}`)].join(','));
        } else if (statusTab === 'under_review') {
          query = query.in('status', UNDER_REVIEW_STATUSES);
        } else {
          query = query.in('status', CLOSED_STATUSES);
        }
        return query;
      };

      const all = await fetchAllPages(buildQuery);

      // Apply read-time issue guardrails (org policy)
      const { data: orgRow } = await supabase
        .from('organizations')
        .select('issue_filter_policy, settings')
        .eq('id', organizationId)
        .single();
      const pol = orgRow?.issue_filter_policy || defaultPolicy();
      setPolicy(pol);
      setOutcomeOptions(normalizeOutcomeOptions(orgRow?.settings?.issue_outcome_options));
      // Same rules as every other surface: no lateness, no guardrail-suppressed.
      const suppressed = suppressedIssuesIn(all, pol);
      setSuppressedCount(suppressed.length);
      setSuppressedIssues(suppressed);
      setAllIssues(countableIssues(all, pol));
      loadTutorReviewStates(organizationId, pol).then(setTutorStates);

      setSelectedGroup(null);
      setSubtitle('');
      setSelectedIssueIdx(0);
      setPage(0);
      setBulkSelected(new Set());
    } catch (err) {
      console.error('Error loading issues:', err);
    }
    setLoading(false);
  };

  // Filter issues by search query
  const matchesSearch = (issue) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const fields = [
      issue.sessions?.tutor_name,
      issue.sessions?.student_name,
      issue.why_flagged,
      issue.tutor_quote,
      issue.context,
      issue.summary,
    ];
    return fields.some(f => f && f.toLowerCase().includes(q));
  };

  // Group issues by category then by session
  const buildCategoryGroups = (categoryKey) => {
    const source = showSuppressed ? [...allIssues, ...suppressedIssues] : allIssues;
    const catIssues = source.filter(i => i.category === categoryKey && matchesSearch(i));
    const grouped = [];
    const sessionMap = {};
    catIssues.forEach(issue => {
      const sessionId = issue.sessions?.id || issue.session_id;
      if (!sessionMap[sessionId]) {
        sessionMap[sessionId] = {
          sessionId,
          tutor_name: issue.sessions?.tutor_name || 'Unknown',
          student_name: issue.sessions?.student_name || 'Unknown',
          lesson_timestamp: issue.sessions?.lesson_timestamp,
          category: categoryKey,
          issues: [],
        };
        grouped.push(sessionMap[sessionId]);
      }
      sessionMap[sessionId].issues.push(issue);
    });
    return grouped;
  };

  // Reopen the issue named in the URL once issues have loaded.
  useEffect(() => {
    const issueId = pendingIssueId.current;
    if (!issueId || loading) return;
    pendingIssueId.current = null;
    const issue = allIssues.find(i => String(i.id) === issueId);
    const group = issue && buildCategoryGroups(issue.category).find(g => g.issues.some(i => i.id === issue.id));
    if (!group) {
      const next = new URLSearchParams(searchParams);
      next.delete('issue');
      setSearchParams(next, { replace: true });
      return;
    }
    setExpandedCategories(prev => new Set(prev).add(issue.category));
    handleSelectGroup(group);
    const idx = group.issues.findIndex(i => i.id === issue.id);
    if (idx > 0) {
      setSelectedIssueIdx(idx);
      setCareLevel(issue.care_level || 0);
      setProposedOutcome(issue.resolution_outcome || '');
      setResolutionNotes(issue.resolution_notes || '');
      loadBreaches(issue.id);
    }
  }, [allIssues, loading]);

  const toggleCategory = (catKey) => {
    const next = new Set(expandedCategories);
    if (next.has(catKey)) next.delete(catKey);
    else next.add(catKey);
    setExpandedCategories(next);
  };

  // Flat list of all visible groups for keyboard nav
  const allVisibleGroups = [];
  CATEGORIES.forEach(cat => {
    if (expandedCategories.has(cat.key)) {
      buildCategoryGroups(cat.key).forEach(g => allVisibleGroups.push(g));
    }
  });

  const selectedGroupIdx = allVisibleGroups.findIndex(g => g.sessionId === selectedGroup?.sessionId && g.category === selectedGroup?.category);

  const updateStatus = async (issueId, newStatus) => {
    try {
      const { error } = await supabase
        .from('risks_v2')
        .update({ status: newStatus, resolved_at: newStatus === 'resolved' || newStatus === 'dismissed' ? new Date().toISOString() : null })
        .eq('id', issueId);
      if (error) throw error;
      toast.success(`Issue ${newStatus.replace(/_/g, ' ')}`);
      loadIssues();
      loadAllStatusCounts();
    } catch (err) {
      toast.error('Failed to update issue');
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleSelectGroup = (group) => {
    setSelectedGroup(group);
    setSelectedIssueIdx(0);
    setCareLevel(group.issues[0]?.care_level || 0);
    setProposedOutcome(group.issues[0]?.resolution_outcome || '');
    setResolutionNotes(group.issues[0]?.resolution_notes || '');
    setSubtitle(`Lesson between ${group.tutor_name} and ${group.student_name} on ${formatDate(group.lesson_timestamp)}`);
    loadBreaches(group.issues[0]?.id);
  };

  // Clear subtitle on unmount
  useEffect(() => {
    return () => setSubtitle('');
  }, [setSubtitle]);

  const handleSelectIssueInGroup = (idx) => {
    setSelectedIssueIdx(idx);
    const issue = selectedGroup.issues[idx];
    setCareLevel(issue?.care_level || 0);
    setProposedOutcome(issue?.resolution_outcome || '');
    setResolutionNotes(issue?.resolution_notes || '');
    loadBreaches(issue?.id);
  };

  const selectedIssue = selectedGroup?.issues[selectedIssueIdx] || null;

  // Mirror the tab and open issue into the URL (replace, so Back still leaves the page).
  useEffect(() => {
    if (pendingIssueId.current) return;
    const next = new URLSearchParams(searchParams);
    if (statusTab === 'open') next.delete('tab'); else next.set('tab', statusTab);
    if (selectedIssue?.id) next.set('issue', String(selectedIssue.id)); else next.delete('issue');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [statusTab, selectedIssue?.id]);

  const saveCareAndOutcome = async () => {
    if (!selectedIssue) return;
    setSaving(true);
    try {
      const isNonIssue = proposedOutcome === 'no_action';
      const hasOutcome = !!proposedOutcome;
      const nextStatus = hasOutcome ? (isNonIssue ? 'resolved' : 'under_review') : selectedIssue.status;
      const update = {
        care_level: careLevel > 0 ? careLevel : null,
        resolution_outcome: proposedOutcome || null,
        resolution_notes: resolutionNotes || null,
      };
      if (hasOutcome) {
        update.status = nextStatus;
        if (isNonIssue) update.resolved_at = new Date().toISOString();
      }
      const { error } = await supabase
        .from('risks_v2')
        .update(update)
        .eq('id', selectedIssue.id);
      if (error) throw error;
      if (hasOutcome) {
        toast.success(isNonIssue ? 'Marked as non-issue — closed' : 'Moved to Under Review');
        loadIssues();
        loadAllStatusCounts();
      } else {
        toast.success('Saved');
      }
    } catch (err) {
      console.error('Save failed:', err);
      const parts = [err?.code, err?.message, err?.details, err?.hint].filter(Boolean);
      const detail = parts.length ? parts.join(' — ') : 'unknown error';
      const truncated = detail.length > 240 ? `${detail.slice(0, 240)}…` : detail;
      toast.error(`Failed to save: ${truncated}`);
    }
    setSaving(false);
  };

  const postNoteToLesson = async () => {
    if (!selectedIssue) return;
    const body = (resolutionNotes || '').trim();
    if (!body) { toast.error('Add note text first'); return; }
    try {
      const sessionId = selectedIssue.sessions?.id || selectedIssue.session_id;
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from('lesson_notes').insert({
        session_id: sessionId,
        organization_id: organizationId,
        body,
        source_risk_id: selectedIssue.id,
        created_by: user?.id || null,
      });
      if (error) throw error;
      toast.success('Note posted to lesson');
    } catch (err) {
      console.error('Post note failed:', err);
      const parts = [err?.code, err?.message, err?.details].filter(Boolean);
      toast.error(`Failed to post: ${parts.join(' — ') || 'unknown error'}`);
    }
  };

  // Load breaches + cached AI result for the selected issue
  const loadBreaches = async (riskId) => {
    if (!riskId) {
      setBreaches([]); setBreachResult(null); setBreachResultAt(null);
      setCurriculumResult(null); setCurriculumResultAt(null);
      return;
    }
    setBreachLoading(true);
    try {
      const [matchesRes, riskRes] = await Promise.all([
        supabase
          .from('breach_matches')
          .select('*, document_sections(section_number, title, content), policy_documents(name, document_type)')
          .eq('risk_id', riskId)
          .order('match_confidence', { ascending: false }),
        supabase
          .from('risks_v2')
          .select('last_breach_check_result, last_breach_check_at, curriculum_check_result, curriculum_check_at')
          .eq('id', riskId)
          .single(),
      ]);
      if (matchesRes.error) throw matchesRes.error;
      setBreaches(matchesRes.data || []);
      setBreachResult(riskRes.data?.last_breach_check_result || null);
      setBreachResultAt(riskRes.data?.last_breach_check_at || null);
      setCurriculumResult(riskRes.data?.curriculum_check_result || null);
      setCurriculumResultAt(riskRes.data?.curriculum_check_at || null);
    } catch (err) {
      console.error('Error loading breaches:', err);
      setBreaches([]);
      setBreachResult(null);
      setBreachResultAt(null);
      setCurriculumResult(null);
      setCurriculumResultAt(null);
    }
    setBreachLoading(false);
  };

  // Run AI-powered breach check via the new Library-backed edge function.
  const runBreachCheck = async () => {
    if (!selectedIssue || !organizationId) return;
    if (libraryReadyCount === 0) {
      toast.error('Upload policies to your Library first');
      navigate('/library');
      return;
    }
    setBreachRunning(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const res = await fetch(`${FUNCTIONS_URL}/breach-check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({ risk_id: selectedIssue.id }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || data?.detail || `Request failed (${res.status})`);
      }
      const result = data.result;
      setBreachResult(result);
      setBreachResultAt(new Date().toISOString());
      if (result?.verdict === 'breach') {
        toast.success(`Breach detected (${result.breached_clauses.length} clause${result.breached_clauses.length !== 1 ? 's' : ''})`);
      } else if (result?.verdict === 'no_breach') {
        toast('No breach detected.');
      } else {
        toast(result?.summary || 'Inconclusive result.');
      }
    } catch (err) {
      console.error('Breach check error:', err);
      toast.error('Breach check failed: ' + (err.message || 'Unknown error'));
    }
    setBreachRunning(false);
  };

  // Run AI-powered curriculum check for flagged teaching errors.
  const runCurriculumCheck = async () => {
    if (!selectedIssue || !organizationId) return;
    if (curriculumReadyCount === 0) {
      toast.error('Upload curriculum documents to your Library first');
      navigate('/library');
      return;
    }
    setCurriculumRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke('curriculum-check', {
        body: { risk_id: selectedIssue.id },
      });
      if (error) throw error;

      // Expected shape: { results: [{ risk_id, result: {...} }] } — be defensive.
      let result = null;
      if (Array.isArray(data?.results)) {
        const row = data.results.find(r => r.risk_id === selectedIssue.id) || data.results[0];
        result = row?.result || null;
      } else if (data?.result?.verdict) {
        result = data.result;
      } else if (data?.verdict) {
        result = data;
      }

      if (result?.verdict) {
        setCurriculumResult(result);
        setCurriculumResultAt(new Date().toISOString());
      } else {
        // Unexpected response shape — fall back to refetching the risk row.
        const { data: riskRow } = await supabase
          .from('risks_v2')
          .select('curriculum_check_result, curriculum_check_at')
          .eq('id', selectedIssue.id)
          .single();
        result = riskRow?.curriculum_check_result || null;
        setCurriculumResult(result);
        setCurriculumResultAt(riskRow?.curriculum_check_at || null);
      }

      if (result?.verdict === 'contradicts_curriculum') {
        toast.success('Contradicts curriculum');
      } else if (result?.verdict === 'consistent_with_curriculum') {
        toast('Aligns with curriculum.');
      } else if (result?.verdict === 'not_covered') {
        toast('Not covered by curriculum.');
      } else if (result?.verdict === 'no_curriculum') {
        toast('No curriculum uploaded.');
      } else {
        toast(result?.summary || 'Curriculum check complete.');
      }
    } catch (err) {
      console.error('Curriculum check error:', err);
      toast.error('Curriculum check failed: ' + (err.message || 'Unknown error'));
    }
    setCurriculumRunning(false);
  };

  // Update breach status (confirm/dismiss)
  const updateBreachStatus = async (breachId, newStatus) => {
    try {
      const { error } = await supabase
        .from('breach_matches')
        .update({ status: newStatus, reviewed_at: new Date().toISOString() })
        .eq('id', breachId);
      if (error) throw error;
      setBreaches(prev => prev.map(b => b.id === breachId ? { ...b, status: newStatus } : b));
      toast.success(`Breach ${newStatus.replace(/_/g, ' ')}`);
    } catch (err) {
      toast.error('Failed to update breach');
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!selectedGroup && allVisibleGroups.length > 0) {
          handleSelectGroup(allVisibleGroups[0]);
        } else if (selectedGroup && selectedGroupIdx < allVisibleGroups.length - 1) {
          handleSelectGroup(allVisibleGroups[selectedGroupIdx + 1]);
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (selectedGroup && selectedGroupIdx > 0) {
          handleSelectGroup(allVisibleGroups[selectedGroupIdx - 1]);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedGroup, selectedGroupIdx, allVisibleGroups]);

  // Bulk edit
  const toggleBulkSelect = (sessionId, catKey) => {
    const key = `${catKey}:${sessionId}`;
    const next = new Set(bulkSelected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setBulkSelected(next);
  };

  const executeBulkAction = async () => {
    if (!bulkAction || bulkSelected.size === 0) return;
    const issueIds = [];
    bulkSelected.forEach(key => {
      const [catKey, sessionId] = key.split(':');
      const groups = buildCategoryGroups(catKey);
      const group = groups.find(g => g.sessionId === sessionId);
      if (group) group.issues.forEach(i => issueIds.push(i.id));
    });

    const newStatus = bulkAction === 'dismiss' ? 'dismissed' : bulkAction;
    try {
      const { error } = await supabase
        .from('risks_v2')
        .update({
          status: newStatus,
          resolved_at: ['resolved', 'dismissed'].includes(newStatus) ? new Date().toISOString() : null,
        })
        .in('id', issueIds);
      if (error) throw error;
      toast.success(`${issueIds.length} issue${issueIds.length > 1 ? 's' : ''} updated`);
      setBulkSelected(new Set());
      setBulkAction('');
      loadIssues();
      loadAllStatusCounts();
    } catch (err) {
      toast.error('Bulk update failed');
    }
  };

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;

  return (
    <div className="space-y-5">
      <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
        Flagged Issues
      </h1>

      {/* Status Tabs + Bulk Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex gap-4">
          {STATUSES.map(s => (
            <button
              key={s.key}
              onClick={() => { setStatusTab(s.key); }}
              className={`text-sm font-medium flex items-center gap-1.5 pb-1 transition-colors ${
                statusTab === s.key
                  ? isDark ? 'text-white' : 'text-[#1a1a1a]'
                  : isDark ? 'text-[#52525b] hover:text-[#a1a1aa]' : 'text-[#999999] hover:text-[#666666]'
              }`}
            >
              {s.label}
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                statusTab === s.key
                  ? isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]'
                  : isDark ? 'bg-[#18181b] text-[#52525b]' : 'bg-[#f0f0f0] text-[#999]'
              }`}>
                {statusCounts[s.key] || 0}
              </span>
            </button>
          ))}
        </div>
        <button
          onClick={() => { setBulkMode(!bulkMode); setBulkSelected(new Set()); setBulkAction(''); }}
          className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
            bulkMode
              ? 'accent-gradient text-white'
              : isDark ? 'text-[#a1a1aa] hover:text-white border border-[#27272a] hover:border-[#3f3f46]' : 'text-[#666] hover:text-[#1a1a1a] border border-[#e5e5e5] hover:border-[#d0d0d0]'
          }`}
        >
          {bulkMode ? 'Cancel bulk edit' : 'Bulk edit'}
        </button>
      </div>

      {/* Bulk Action Bar */}
      {bulkMode && bulkSelected.size > 0 && (
        <div className={`flex items-center gap-3 p-3 rounded-lg border ${isDark ? 'bg-[#18181b] border-[#27272a]' : 'bg-[#f5f5f5] border-[#e5e5e5]'}`}>
          <span className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{bulkSelected.size} selected</span>
          <select
            value={bulkAction}
            onChange={e => setBulkAction(e.target.value)}
            className={`h-8 px-2 text-xs rounded-lg border outline-none ${isDark ? 'bg-[#121212] border-[#27272a] text-white' : 'bg-white border-[#e5e5e5] text-[#1a1a1a]'}`}
          >
            {BULK_ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
          <Button size="sm" onClick={executeBulkAction} disabled={!bulkAction}
            className={isDark ? 'accent-gradient text-white' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'}>
            Apply
          </Button>
        </div>
      )}

      {/* Category Filter Tabs */}
      <div className={`flex items-center gap-1 rounded-lg border p-1 ${isDark ? 'border-[#27272a] bg-[#121212]' : 'border-[#e5e5e5] bg-white'}`}>
        <button
          onClick={() => setCategoryFilter('all')}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            categoryFilter === 'all'
              ? isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]'
              : isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'
          }`}
        >
          All
        </button>
        {CATEGORIES.map(cat => {
          const count = allIssues.filter(i => i.category === cat.key && matchesSearch(i)).length;
          return (
            <button
              key={cat.key}
              onClick={() => { setCategoryFilter(cat.key); setExpandedCategories(new Set([cat.key])); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                categoryFilter === cat.key
                  ? isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]'
                  : isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'
              }`}
            >
              {cat.label}
              {count > 0 && <span className={`text-[10px] ${categoryFilter === cat.key ? '' : 'opacity-50'}`}>{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
        <input
          type="text"
          placeholder="Search issues by tutor, student, quote, context..."
          value={searchQuery}
          onChange={e => { setSearchQuery(e.target.value); setCategoryPages({}); }}
          className={`w-full pl-9 pr-8 py-2 text-sm rounded-lg border outline-none transition-colors ${
            isDark ? 'bg-[#121212] border-[#27272a] text-white placeholder-[#52525b] focus:border-[#3f3f46]' : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder-[#999] focus:border-[#d0d0d0]'
          }`}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-[#52525b] hover:text-white' : 'text-[#999] hover:text-[#1a1a1a]'}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Suppressed-by-guardrails chip */}
      {suppressedCount > 0 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => { setShowSuppressed(v => !v); setCategoryPages({}); }}
            className={`inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
              showSuppressed
                ? isDark ? 'border-[#3f3f46] bg-[#18181b] text-white' : 'border-[#d0d0d0] bg-[#f0f0f0] text-[#1a1a1a]'
                : isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:border-[#d0d0d0]'
            }`}
            title={policy?.summary || 'Issue guardrails policy'}
          >
            {showSuppressed ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            {showSuppressed ? `Showing ${suppressedCount} suppressed by guardrails` : `${suppressedCount} suppressed by guardrails`}
          </button>
          <button
            onClick={() => navigate('/settings/issue-guardrails')}
            className={`text-xs ${isDark ? 'text-[#586f8d] hover:text-white' : 'text-[#586f8d] hover:text-[#1a1a1a]'}`}
          >
            Manage guardrails →
          </button>
        </div>
      )}

      {/* Main Layout: Left list + Right detail */}
      <div className="flex gap-4">
        {/* Left Panel — Categories with collapsible issue lists */}
        <div className={`${cardClass} w-[40%] flex-shrink-0 overflow-y-auto`} style={{ maxHeight: 'calc(100vh - 220px)' }}>
          {loading ? (
            <div className={`animate-pulse divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`} aria-busy="true" aria-label="Loading issues">
              {[0, 1, 2, 3].map(g => (
                <div key={g} className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className={`h-4 w-40 rounded ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`} />
                    <div className={`h-5 w-6 rounded ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`} />
                  </div>
                  {g < 2 && [0, 1].map(r => (
                    <div key={r} className="pl-1 space-y-2">
                      <div className={`h-3.5 w-3/4 rounded ${isDark ? 'bg-[#1f1f23]' : 'bg-[#efefef]'}`} />
                      <div className={`h-3 w-16 rounded ${isDark ? 'bg-[#1f1f23]' : 'bg-[#efefef]'}`} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className={`divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
              {CATEGORIES.filter(cat => categoryFilter === 'all' || categoryFilter === cat.key).map(cat => {
                const groups = buildCategoryGroups(cat.key);
                const isExpanded = expandedCategories.has(cat.key);
                const count = groups.reduce((sum, g) => sum + g.issues.length, 0);
                const catPage = categoryPages[cat.key] || 0;
                const totalCatPages = Math.ceil(groups.length / PER_CATEGORY_PAGE);
                const pagedGroups = groups.slice(catPage * PER_CATEGORY_PAGE, (catPage + 1) * PER_CATEGORY_PAGE);

                return (
                  <div key={cat.key}>
                    {/* Category Header */}
                    <button
                      onClick={() => toggleCategory(cat.key)}
                      className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'}`}
                    >
                      <div className="flex items-center gap-2">
                        {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-[#52525b]" /> : <ChevronDown className="h-3.5 w-3.5 text-[#52525b]" />}
                        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{cat.label}</span>
                      </div>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        count > 0
                          ? isDark ? 'bg-[#27272a] text-[#a1a1aa]' : 'bg-[#e8e8e8] text-[#666]'
                          : isDark ? 'bg-[#18181b] text-[#52525b]' : 'bg-[#f0f0f0] text-[#999]'
                      }`}>
                        {count}
                      </span>
                    </button>

                    {/* Expanded Issues */}
                    {isExpanded && (
                      <div className={`${isDark ? 'bg-[#0d0d0f]' : 'bg-[#fafafa]'}`}>
                        {groups.length === 0 ? (
                          <div className={`px-4 py-3 text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                            No {statusTab} issues
                          </div>
                        ) : (
                          <>
                          {pagedGroups.map(group => {
                            const isSelected = selectedGroup?.sessionId === group.sessionId && selectedGroup?.category === group.category;
                            const issueCount = group.issues.length;
                            const ts = group.lesson_timestamp ? new Date(group.lesson_timestamp) : null;
                            const dateStr = ts ? ts.toLocaleDateString([], { day: 'numeric', month: 'short' }) : '';
                            const bulkKey = `${cat.key}:${group.sessionId}`;
                            const isBulkChecked = bulkSelected.has(bulkKey);

                            return (
                              <div
                                key={group.sessionId}
                                className={`flex items-start transition-colors border-t ${
                                  isDark ? 'border-[#1a1a1a]' : 'border-[#f0f0f0]'
                                } ${
                                  isSelected
                                    ? isDark ? 'bg-[#18181b] border-l-2 border-l-[#586f8d]' : 'bg-[#f5f5f5] border-l-2 border-l-[#586f8d]'
                                    : isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#f5f5f5]'
                                }`}
                              >
                                {bulkMode && (
                                  <button
                                    onClick={() => toggleBulkSelect(group.sessionId, cat.key)}
                                    className={`pl-4 pt-3 flex-shrink-0 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}
                                  >
                                    {isBulkChecked ? <CheckSquare className="h-3.5 w-3.5 text-[#586f8d]" /> : <Square className="h-3.5 w-3.5" />}
                                  </button>
                                )}
                                <button
                                  onClick={() => handleSelectGroup(group)}
                                  className="w-full text-left px-4 py-3"
                                >
                                  <div className="flex items-center justify-between">
                                    <span className={`text-xs font-medium ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                                      {group.tutor_name} → {group.student_name}
                                    </span>
                                    <span className={`text-[10px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{dateStr}</span>
                                  </div>
                                  <span className={`text-[10px] mt-0.5 font-medium ${
                                    isDark ? 'text-[#52525b]' : 'text-[#999]'
                                  }`}>
                                    {issueCount} issue{issueCount !== 1 ? 's' : ''}
                                  </span>
                                  {statusTab !== 'under_review' && tutorStates[group.tutor_name] === 'under_review' && (
                                    <span
                                      title="This tutor already has an issue under review"
                                      className={`ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${isDark ? 'bg-sky-500/15 text-sky-300' : 'bg-sky-50 text-sky-700'}`}
                                    >
                                      Tutor under review
                                    </span>
                                  )}
                                </button>
                              </div>
                            );
                          })}
                          {totalCatPages > 1 && (
                            <div className={`flex items-center justify-center gap-2 py-2 border-t ${isDark ? 'border-[#1a1a1a]' : 'border-[#f0f0f0]'}`}>
                              <button
                                onClick={() => setCategoryPages(p => ({ ...p, [cat.key]: Math.max(0, catPage - 1) }))}
                                disabled={catPage === 0}
                                className={`p-1 rounded disabled:opacity-30 ${isDark ? 'hover:bg-[#18181b]' : 'hover:bg-[#f0f0f0]'}`}
                              >
                                <ChevronLeft className="h-3 w-3" />
                              </button>
                              <span className={`text-[10px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                                {catPage + 1} / {totalCatPages}
                              </span>
                              <button
                                onClick={() => setCategoryPages(p => ({ ...p, [cat.key]: Math.min(totalCatPages - 1, catPage + 1) }))}
                                disabled={catPage >= totalCatPages - 1}
                                className={`p-1 rounded disabled:opacity-30 ${isDark ? 'hover:bg-[#18181b]' : 'hover:bg-[#f0f0f0]'}`}
                              >
                                <ChevronRight className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail Panel — 60% */}
        <div className={`${cardClass} flex-1 min-w-0 ${selectedGroup ? 'p-5' : 'flex items-center justify-center'}`} style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
          {!selectedGroup ? (
            <div className={`text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              Select a lesson to view issues · Use ↑↓ arrow keys to navigate
            </div>
          ) : (
            <div className="space-y-5">
              {/* Header */}
              <div className="flex items-center justify-between">
                <Link
                  to={`/lessons/${selectedGroup.sessionId}`}
                  state={{ from: `/issues?${searchParams.toString()}`, fromLabel: 'Back to issue' }}
                  title="Open this lesson"
                  className={`group flex items-center gap-1.5 text-left text-base font-medium font-[Manrope] transition-colors ${
                    isDark ? 'text-white hover:text-[#7662ac]' : 'text-[#1a1a1a] hover:text-[#586f8d]'
                  }`}
                >
                  <span className="underline decoration-transparent group-hover:decoration-current underline-offset-4">
                    {selectedGroup.tutor_name} → {selectedGroup.student_name}
                  </span>
                  <ExternalLink className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
                {tutorStates[selectedGroup.tutor_name] === 'under_review' && (
                  <span
                    title="This tutor already has an issue under review"
                    className={`ml-2 mr-auto inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${isDark ? 'bg-sky-500/15 text-sky-300' : 'bg-sky-50 text-sky-700'}`}
                  >
                    Tutor under review
                  </span>
                )}
                <button onClick={() => { setSelectedGroup(null); setSubtitle(''); }} className={isDark ? 'text-[#52525b] hover:text-white' : 'text-[#999] hover:text-[#1a1a1a]'}>
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Issue sub-tabs if multiple issues */}
              {selectedGroup.issues.length > 1 && (
                <div className={`flex gap-1 rounded-lg border p-1 overflow-x-auto ${isDark ? 'border-[#27272a] bg-[#0d0d0f]' : 'border-[#e5e5e5] bg-[#fafafa]'}`}>
                  {selectedGroup.issues.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSelectIssueInGroup(idx)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                        selectedIssueIdx === idx
                          ? isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]'
                          : isDark ? 'text-[#52525b] hover:text-[#a1a1aa]' : 'text-[#999] hover:text-[#666]'
                      }`}
                    >
                      Issue {idx + 1}
                    </button>
                  ))}
                </div>
              )}

              {selectedIssue && (
                <>
                  {/* Why Flagged */}
                  {selectedIssue.why_flagged && (
                    <p className={`text-sm leading-relaxed ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                      {selectedIssue.why_flagged}
                    </p>
                  )}

                  {/* Evidence */}
                  {(selectedIssue.tutor_quote || selectedIssue.context) && (
                    <div>
                      <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Evidence</div>
                      {selectedIssue.tutor_quote && (
                        <div className="mb-3">
                          <div className={`text-xs mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Quote:</div>
                          <p className={`text-sm italic p-3 rounded-lg border-l-2 ${isDark ? 'bg-[#18181b] text-[#a1a1aa] border-[#586f8d]' : 'bg-[#f5f5f5] text-[#666] border-[#586f8d]'}`}>
                            "{selectedIssue.tutor_quote}"
                          </p>
                        </div>
                      )}
                      {selectedIssue.context && (
                        <div>
                          <div className={`text-xs mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Context:</div>
                          <p className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{selectedIssue.context}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Watch Recording + Transcript links */}
                  {(() => {
                    const rawSeconds = selectedIssue.timestamp_seconds ?? 0;
                    const watchSeconds = Math.max(0, rawSeconds - 15);
                    const formatTs = (s) => { const m = Math.floor(s / 60); const sec = Math.floor(s % 60); return `${m}:${sec.toString().padStart(2, '0')}`; };
                    let watchUrl = null;
                    if (selectedIssue.playback_url) {
                      const base = selectedIssue.playback_url;
                      if (/[?&]ts=\d+/.test(base)) {
                        watchUrl = base.replace(/([?&])ts=\d+/, `$1ts=${watchSeconds}`);
                      } else {
                        watchUrl = base + (base.includes('?') ? '&' : '?') + `ts=${watchSeconds}`;
                      }
                    }
                    return (
                      <div className="flex flex-col gap-2">
                        {watchUrl && (
                          <a
                            href={watchUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center gap-1.5 text-xs ${isDark ? 'text-[#7662ac] hover:text-white' : 'text-[#586f8d] hover:text-[#1a1a1a]'}`}
                          >
                            <ExternalLink className="h-3.5 w-3.5" /> Watch Recording ({formatTs(watchSeconds)})
                          </a>
                        )}
                        {selectedIssue.timestamp_display && (
                          <Link
                            to={`/lessons/${selectedGroup.sessionId}?tab=transcript&t=${encodeURIComponent(selectedIssue.timestamp_display)}`}
                            state={{ from: `/issues?${searchParams.toString()}`, fromLabel: 'Back to issue' }}
                            title="Open transcript (Ctrl/Cmd-click for a new tab)"
                            className={`flex items-center gap-1.5 text-xs ${isDark ? 'text-[#7662ac] hover:text-white' : 'text-[#586f8d] hover:text-[#1a1a1a]'}`}
                          >
                            <FileText className="h-3.5 w-3.5" /> Go to transcript ({selectedIssue.timestamp_display})
                          </Link>
                        )}
                      </div>
                    );
                  })()}

                  {/* Policy Breaches */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className={`text-xs font-medium uppercase tracking-wider flex items-center gap-1.5 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                        <Shield className="h-3.5 w-3.5" />
                        Policy Breaches
                        {breaches.length > 0 && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-[#27272a] text-[#7662ac]' : 'bg-[#fef3cd] text-[#586f8d]'}`}>
                            {breaches.length}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={runBreachCheck}
                        disabled={breachRunning || libraryReadyCount === 0}
                        title={libraryReadyCount === 0 ? 'Upload policies to your Library first' : ''}
                        className={`text-xs flex items-center gap-1 px-2 py-1 rounded-md transition-colors ${
                          breachRunning || libraryReadyCount === 0
                            ? isDark ? 'text-[#52525b] cursor-not-allowed' : 'text-[#999] cursor-not-allowed'
                            : isDark ? 'text-[#7662ac] hover:bg-[#18181b]' : 'text-[#586f8d] hover:bg-[#f5f5f5]'
                        }`}
                      >
                        {breachRunning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Shield className="h-3 w-3" />}
                        {breachRunning ? 'Analysing...' : (breachResult || breaches.length > 0) ? 'Re-check' : 'Run Breach Check'}
                      </button>
                    </div>

                    {libraryReadyCount === 0 && (
                      <button
                        onClick={() => navigate('/library')}
                        className={`text-[11px] mb-2 underline ${isDark ? 'text-[#7662ac] hover:text-white' : 'text-[#586f8d] hover:text-[#1a1a1a]'}`}
                      >
                        Upload policies to your Library first →
                      </button>
                    )}

                    {breachResult && (
                      (() => {
                        const v = breachResult.verdict;
                        const hasBreach = v === 'breach' && Array.isArray(breachResult.breached_clauses) && breachResult.breached_clauses.length > 0;
                        if (!hasBreach) {
                          return (
                            <div className={`mb-3 p-3 rounded-lg border text-xs flex items-center justify-between ${isDark ? 'bg-[#0d0d0f] border-[#27272a] text-[#a1a1aa]' : 'bg-white border-[#e5e5e5] text-[#666]'}`}>
                              <span>No policy breaches found.</span>
                              {breachResultAt && (
                                <span className={`text-[10px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                                  {new Date(breachResultAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </div>
                          );
                        }
                        const conf = Math.round((breachResult.confidence || 0) * 100);
                        return (
                          <div className={`mb-3 p-3 rounded-lg border text-xs ${isDark ? 'bg-[#0d0d0f] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-600'}`}>Breach</span>
                                <span className={`text-[11px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{conf}% confidence</span>
                              </div>
                              {breachResultAt && (
                                <span className={`text-[10px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                                  {new Date(breachResultAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </div>
                            {breachResult.summary && (
                              <p className={`mb-2 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{breachResult.summary}</p>
                            )}
                            <div className="space-y-2">
                              {breachResult.breached_clauses.map((c, i) => (
                                <details key={i} className={`rounded-md border ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                                  <summary className={`px-2 py-1.5 cursor-pointer text-[11px] font-medium ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`}>
                                    {c.document_title || 'Cited clause'}
                                  </summary>
                                  <div className="px-2 py-2 space-y-2">
                                    {c.chunk_excerpt && (
                                      <blockquote className={`border-l-2 pl-2 italic ${isDark ? 'border-[#3f3f46] text-[#a1a1aa]' : 'border-[#d4d4d4] text-[#555]'}`}>
                                        {c.chunk_excerpt}
                                      </blockquote>
                                    )}
                                    {c.reasoning && (
                                      <p className={isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}>{c.reasoning}</p>
                                    )}
                                  </div>
                                </details>
                              ))}
                            </div>
                          </div>
                        );
                      })()
                    )}

                    {breachLoading ? (
                      <div className={`text-xs py-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Loading breaches...</div>
                    ) : breaches.length > 0 ? (
                      <div className="space-y-2">
                        {breaches.map(breach => (
                          <div
                            key={breach.id}
                            className={`p-3 rounded-lg border text-xs ${
                              breach.status === 'confirmed'
                                ? isDark ? 'bg-[#1a1510] border-[#586f8d]/30' : 'bg-[#fef9f0] border-[#e8edf2]'
                                : breach.status === 'dismissed'
                                ? isDark ? 'bg-[#18181b] border-[#27272a] opacity-50' : 'bg-[#fafafa] border-[#e5e5e5] opacity-50'
                                : isDark ? 'bg-[#18181b] border-[#27272a]' : 'bg-[#fafafa] border-[#e5e5e5]'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <button
                                onClick={() => {
                                  const docId = breach.document_id;
                                  if (docId) navigate(`/policies/${docId}`);
                                }}
                                className={`font-medium hover:underline text-left ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`}
                              >
                                {breach.reference_label || 'Policy Section'}
                              </button>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  breach.match_confidence >= 0.6
                                    ? isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-600'
                                    : breach.match_confidence >= 0.35
                                    ? isDark ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-50 text-yellow-700'
                                    : isDark ? 'bg-[#27272a] text-[#a1a1aa]' : 'bg-[#f0f0f0] text-[#666]'
                                }`}>
                                  {Math.round(breach.match_confidence * 100)}%
                                </span>
                              </div>
                            </div>
                            <p className={`mb-2 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                              {breach.breach_explanation}
                            </p>
                            {breach.status === 'pending' && (
                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={() => updateBreachStatus(breach.id, 'confirmed')}
                                  className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${isDark ? 'hover:bg-[#27272a] text-[#a1a1aa]' : 'hover:bg-[#e8e8e8] text-[#666]'}`}
                                >
                                  <CheckCircle className="h-3 w-3 text-green-500" /> Confirm
                                </button>
                                <button
                                  onClick={() => updateBreachStatus(breach.id, 'dismissed')}
                                  className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${isDark ? 'hover:bg-[#27272a] text-[#a1a1aa]' : 'hover:bg-[#e8e8e8] text-[#666]'}`}
                                >
                                  <XCircle className="h-3 w-3 text-red-400" /> Dismiss
                                </button>
                              </div>
                            )}
                            {breach.status !== 'pending' && (
                              <div className={`flex items-center gap-1 ${breach.status === 'confirmed' ? 'text-green-500' : isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                                {breach.status === 'confirmed' ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                <span className="capitalize">{breach.status}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                        No policy breaches detected.
                      </p>
                    )}
                  </div>

                  {/* Curriculum Alignment */}
                  {selectedIssue.category === 'factual_teaching_error' && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className={`text-xs font-medium uppercase tracking-wider flex items-center gap-1.5 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                          <BookOpen className="h-3.5 w-3.5" />
                          Curriculum Alignment
                        </div>
                        <button
                          onClick={runCurriculumCheck}
                          disabled={curriculumRunning || curriculumReadyCount === 0}
                          title={curriculumReadyCount === 0 ? 'Upload curriculum documents to your Library first' : ''}
                          className={`text-xs flex items-center gap-1 px-2 py-1 rounded-md transition-colors ${
                            curriculumRunning || curriculumReadyCount === 0
                              ? isDark ? 'text-[#52525b] cursor-not-allowed' : 'text-[#999] cursor-not-allowed'
                              : isDark ? 'text-[#7662ac] hover:bg-[#18181b]' : 'text-[#586f8d] hover:bg-[#f5f5f5]'
                          }`}
                        >
                          {curriculumRunning ? <Loader2 className="h-3 w-3 animate-spin" /> : <BookOpen className="h-3 w-3" />}
                          {curriculumRunning ? 'Analysing...' : curriculumResult ? 'Re-check' : 'Run Curriculum Check'}
                        </button>
                      </div>

                      {curriculumReadyCount === 0 && (
                        <button
                          onClick={() => navigate('/library')}
                          className={`text-[11px] mb-2 underline ${isDark ? 'text-[#7662ac] hover:text-white' : 'text-[#586f8d] hover:text-[#1a1a1a]'}`}
                        >
                          Upload curriculum documents to your Library first →
                        </button>
                      )}

                      {breachLoading ? (
                        <div className={`text-xs py-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Loading curriculum check...</div>
                      ) : curriculumResult ? (
                        (() => {
                          const v = curriculumResult.verdict;
                          const badge = {
                            contradicts_curriculum: { label: 'Contradicts curriculum', cls: isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-600' },
                            consistent_with_curriculum: { label: 'Aligns with curriculum', cls: isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-600' },
                            not_covered: { label: 'Not covered by curriculum', cls: isDark ? 'bg-[#27272a] text-[#a1a1aa]' : 'bg-[#f0f0f0] text-[#666]' },
                            no_curriculum: { label: 'No curriculum uploaded', cls: isDark ? 'bg-[#18181b] text-[#52525b]' : 'bg-[#f7f7f7] text-[#999]' },
                          }[v] || { label: v || 'Unknown result', cls: isDark ? 'bg-[#27272a] text-[#a1a1aa]' : 'bg-[#f0f0f0] text-[#666]' };
                          const conf = typeof curriculumResult.confidence === 'number' ? Math.round(curriculumResult.confidence * 100) : null;
                          const citations = Array.isArray(curriculumResult.citations) ? curriculumResult.citations : [];
                          return (
                            <div className={`p-3 rounded-lg border text-xs ${isDark ? 'bg-[#0d0d0f] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${badge.cls}`}>{badge.label}</span>
                                  {v !== 'no_curriculum' && conf !== null && (
                                    <span className={`text-[11px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{conf}% confidence</span>
                                  )}
                                </div>
                                {curriculumResultAt && (
                                  <span className={`text-[10px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                                    Checked {new Date(curriculumResultAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                )}
                              </div>
                              {v === 'no_curriculum' && (
                                <button
                                  onClick={() => navigate('/library')}
                                  className={`underline mb-2 block text-left ${isDark ? 'text-[#7662ac] hover:text-white' : 'text-[#586f8d] hover:text-[#1a1a1a]'}`}
                                >
                                  Upload curriculum documents in your Library →
                                </button>
                              )}
                              {curriculumResult.summary && (
                                <p className={`mb-2 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{curriculumResult.summary}</p>
                              )}
                              {citations.length > 0 && (
                                <div className="space-y-2">
                                  {citations.map((c, i) => (
                                    <div key={i} className={`rounded-md border px-2 py-2 space-y-1.5 ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                                      <div className={`text-[11px] font-semibold ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                                        {c.document_title || 'Curriculum document'}
                                      </div>
                                      {c.excerpt && (
                                        <blockquote className={`border-l-2 pl-2 italic ${isDark ? 'border-[#3f3f46] text-[#a1a1aa]' : 'border-[#d4d4d4] text-[#555]'}`}>
                                          {c.excerpt}
                                        </blockquote>
                                      )}
                                      {c.reasoning && (
                                        <p className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>{c.reasoning}</p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <p className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                          Not yet checked against curriculum.
                        </p>
                      )}
                    </div>
                  )}

                  <div className={`border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`} />

                  {/* How much do you care */}
                  <div>
                    <div className={`text-xs font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                      How much do you care about this issue?
                    </div>
                    <div className={`text-xs mb-3 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                      Your answers help our models learn about what issues you care most about
                    </div>
                    <div className="relative">
                      {/* Background track */}
                      <div className={`w-full h-2 rounded-full ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`} />
                      {/* Filled portion — single continuous gradient */}
                      <div
                        className="absolute top-0 left-0 h-2 rounded-full transition-all duration-200"
                        style={{
                          width: `${((careLevel + 1) / CARE_LABELS.length) * 100}%`,
                          background: 'linear-gradient(90deg, #586f8d 0%, #7662ac 100%)',
                        }}
                      />
                      {/* Labels */}
                      <div className="flex items-center mt-0">
                        {CARE_LABELS.map((label, idx) => (
                          <button key={idx} onClick={() => setCareLevel(idx)} className="flex-1 pt-1.5 cursor-pointer">
                            <span className={`text-[10px] block text-center ${
                              idx === careLevel ? isDark ? 'text-white' : 'text-[#1a1a1a]' : isDark ? 'text-[#52525b]' : 'text-[#999]'
                            }`}>{label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Resolution Notes */}
                  <div>
                    <div className={`flex items-center justify-between mb-2`}>
                      <div className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                        Notes
                      </div>
                      <button
                        type="button"
                        onClick={postNoteToLesson}
                        disabled={!resolutionNotes?.trim()}
                        title="Post this note to the lesson"
                        className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded-md transition-colors disabled:opacity-40 ${
                          isDark ? 'bg-[#18181b] hover:bg-[#27272a] text-[#a1a1aa] hover:text-white' : 'bg-[#f0f0f0] hover:bg-[#e5e5e5] text-[#555] hover:text-[#1a1a1a]'
                        }`}
                      >
                        <FileText className="h-3 w-3" /> Post to lesson
                      </button>
                    </div>
                    <textarea
                      value={resolutionNotes}
                      onChange={e => setResolutionNotes(e.target.value)}
                      placeholder="Add notes about this issue, actions taken, follow-up needed..."
                      rows={3}
                      className={`w-full px-3 py-2 text-sm rounded-lg border transition-colors outline-none resize-none ${
                        isDark ? 'bg-[#121212] border-[#27272a] text-white placeholder-[#52525b] focus:border-[#3f3f46]' : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder-[#999] focus:border-[#d0d0d0]'
                      }`}
                    />
                  </div>

                  {/* Proposed Action */}
                  <div>
                    <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                      Proposed Action
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={proposedOutcome} onValueChange={setProposedOutcome}>
                        <SelectTrigger className={`flex-1 h-9 text-sm ${isDark ? 'bg-[#121212] border-[#27272a] text-white' : 'bg-white border-[#e5e5e5] text-[#1a1a1a]'}`}>
                          <SelectValue placeholder="Select outcome..." />
                        </SelectTrigger>
                        <SelectContent className={isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}>
                          {outcomeOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={saveCareAndOutcome} disabled={saving}
                        className={isDark ? 'accent-gradient text-white' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'}>
                        {saving ? '...' : 'Save'}
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => updateStatus(selectedIssue.id, 'dismissed')}
                        className={isDark ? 'border-[#27272a] text-[#a1a1aa] hover:bg-[#18181b]' : 'border-[#e5e5e5] text-[#666] hover:bg-[#f5f5f5]'}>
                        Dismiss
                      </Button>
                    </div>
                  </div>

                  {/* Status Actions */}
                  {statusTab === 'under_review' && (
                    <div className="flex gap-2 pt-1 relative">
                      <Button size="sm"
                        onClick={() => {
                          if (!actionedByOpen) { loadActionedByNames(); }
                          setActionedByOpen(o => !o);
                          setAddingNewName(false);
                          setActionedByNew('');
                        }}
                        className={isDark ? 'accent-gradient text-white' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'}>
                        <Check className="h-3.5 w-3.5 mr-1" /> Actioned
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => updateStatus(selectedIssue.id, 'dismissed')}
                        className={isDark ? 'border-[#27272a] text-[#a1a1aa] hover:bg-[#18181b]' : 'border-[#e5e5e5] text-[#666] hover:bg-[#f5f5f5]'}>
                        Dismiss
                      </Button>
                      {actionedByOpen && (
                        <div className={`absolute top-full left-0 mt-2 z-30 w-72 rounded-lg border shadow-lg ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}>
                          <div className={`px-3 py-2 text-[11px] font-medium uppercase tracking-wider border-b ${isDark ? 'text-[#a1a1aa] border-[#27272a]' : 'text-[#666] border-[#e5e5e5]'}`}>
                            Actioned by
                          </div>
                          <div className="max-h-56 overflow-y-auto py-1">
                            {actionedByNames.length === 0 && !addingNewName && (
                              <div className={`px-3 py-2 text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>No saved names yet — add one below.</div>
                            )}
                            {actionedByNames.map(name => (
                              <button
                                key={name}
                                type="button"
                                onClick={() => resolveAsActioned(name)}
                                className={`w-full text-left px-3 py-2 text-sm transition-colors ${isDark ? 'hover:bg-[#18181b] text-white' : 'hover:bg-[#f5f5f5] text-[#1a1a1a]'}`}
                              >
                                {name}
                              </button>
                            ))}
                          </div>
                          <div className={`border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                            {!addingNewName ? (
                              <button
                                type="button"
                                onClick={() => setAddingNewName(true)}
                                className={`w-full text-left px-3 py-2 text-xs font-medium transition-colors ${isDark ? 'text-[#7662ac] hover:bg-[#18181b]' : 'text-[#586f8d] hover:bg-[#f5f5f5]'}`}
                              >
                                + Add new name…
                              </button>
                            ) : (
                              <div className="p-2 flex gap-2">
                                <input
                                  autoFocus
                                  type="text"
                                  value={actionedByNew}
                                  onChange={e => setActionedByNew(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') resolveAsActioned(actionedByNew); }}
                                  placeholder="Full name"
                                  className={`flex-1 text-sm px-2 py-1.5 rounded-md border outline-none ${isDark ? 'bg-[#0d0d0f] border-[#27272a] text-white placeholder-[#52525b]' : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder-[#999]'}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => resolveAsActioned(actionedByNew)}
                                  disabled={!actionedByNew.trim()}
                                  className={`text-xs font-medium px-3 rounded-md disabled:opacity-40 ${isDark ? 'bg-white text-black hover:bg-[#e5e5e5]' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'}`}
                                >
                                  Save
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Issues;
